import { UHttp } from '../../utils/UHttp';
import companyStore from "../../store/company-store";

export type TimeSlotStatus = 'available' | 'unavailable';

export interface Availability{
  id: string;
  professionalId: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  type: string;
  status: string;
  day?: string;
  recurrence?: {
    startDate: Date;
    endDate: Date;
  };
  hasBookings?: boolean;
  bookings?: Array<{
    id: string;
    startTime: string;
    endTime: string;
  }>;
}

export interface AvailabilityResponse {
  success: true,
  status: number,
  message: string,
  data: any
}

type NewAvailability = {
  startDate: string,
  endDate: string,
  startTime: string,
  endTime: string,
  type: string,
  status: string
}

interface OneOffScheduleRequest {
  data: NewAvailability[]
}

export interface TimeRange {
  startTime: string;
  endTime: string;
}

export interface DaySchedule {
  day: number;
  timeRanges: TimeRange[];
}


export interface RecurringScheduleRequest {
  startDate: string;
  endDate: string;
  type: string;
  status: string;
  schedule: DaySchedule[];
}

/**
 * Créneau disponible pour la réservation (structure API)
 */
export interface BookableTimeSlot {
  timeSlotId: string;
  configuredSlotId: string;
  professionalId: string;
  serviceId: string;
  startTime: string;
  endTime: string;
  serviceDuration: number;
}

const apiUrlServer = process.env.REACT_APP_URL_SERVER

/**
 * Modèle pour gérer les interactions avec l'API concernant les disponibilités
 */
export class AvailabilityModel {
  private static instance: AvailabilityModel;

  private constructor() {}

  public static getInstance(): AvailabilityModel {
    if (!AvailabilityModel.instance) {
      AvailabilityModel.instance = new AvailabilityModel();
    }
    return AvailabilityModel.instance;
  }

  /**
   * Récupère les créneaux depuis l'API
   * @returns Promise avec les créneaux
   */
  async getTimeSlotsByProfessional(startDate?: string, endDate?: string, collaboratorCompagnieId?: string): Promise<AvailabilityResponse> {
    try {
      const selectedCompanyId = companyStore.getIdCompanySelectedDefault;

      let url = apiUrlServer + '/api/timeslot/collaborator?companyId=' + selectedCompanyId;

      // Construction des paramètres de requête
      const params = new URLSearchParams();

      if (startDate) {
        params.append('startDate', startDate);
      }

      if (endDate) {
        params.append('endDate', endDate);
      }

      if (collaboratorCompagnieId) {
        params.append('collaboratorCompagnieId', collaboratorCompagnieId);
      }

      if (params.toString()) {
        url += '&' + params.toString();
      }


      const response = await UHttp.get(url, UHttp.authHeaders());
      const responseData = await response.json();

      return {
        ...responseData,
        status: response.status
      };

    } catch (error) {
      throw error;
    }
  }

  /**
   * Crée un nouveau créneau récurrent via l'API
   * @param credentials Données du créneau récurrent
   * @returns Promise avec le créneau créé
   */
  async createRecurringSlot(credentials: RecurringScheduleRequest): Promise<AvailabilityResponse> {
    try {
      const response = await UHttp.post(
          apiUrlServer + "/api/timeslot/recurring",
          credentials,
          UHttp.authHeaders()
      )

      const responseData = await response.json();
      return {
        ...responseData,
        status: response.status
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Crée un nouveau créneau ponctuel via l'API
   * @param credentials Données du créneau ponctuel
   * @returns Promise avec le créneau créé
   */
  async createOneTimeSlot(credentials: OneOffScheduleRequest): Promise<AvailabilityResponse>  {
    try {


      const response = await UHttp.post(
          apiUrlServer + "/api/timeslot",
          credentials,
          UHttp.authHeaders()
      )

      const responseData = await response.json();
      return {
        ...responseData,
        status: response.status
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Supprime un créneau récurrent via l'API
   * @param id Identifiant du créneau récurrent
   * @returns Promise avec un booléen indiquant le succès
   */
  async deleteRecurringSlot(id: string): Promise<AvailabilityResponse> {
    try {
      const selectedCompanyId = companyStore.getIdCompanySelectedDefault;
      const response = await UHttp.delete(
          apiUrlServer + "/api/timeslot/delete/" + id + "?companyId=" + selectedCompanyId,
          UHttp.authHeaders()
      )

      const responseData = await response.json();
      return {
        ...responseData,
        status: response.status
      };
    } catch (error) {
      throw error;
    }
  }

  async updateTimeSlot(credentials: any): Promise<AvailabilityResponse> {
    try {
      const response = await UHttp.put(
          apiUrlServer + "/api/timeslot/update/" + credentials.id,
          credentials,
          UHttp.authHeaders()
      )

      const responseData = await response.json();
      return {
        ...responseData,
        data: [responseData.data],
        status: response.status
      };
    } catch (error) {
      throw error;
    }
  }

  async bulkDeleteTimeSlots(companyId: string, ids: string[]): Promise<AvailabilityResponse> {
    try {
      const response = await UHttp.post(
        apiUrlServer + "/api/timeslot/delete-bulk",
        { companyId, ids },
        UHttp.authHeaders()
      );
      const responseData = await response.json();
      return { ...responseData, status: response.status };
    } catch (error) {
      throw error;
    }
  }

  async bulkUpdateTimeSlots(companyId: string, updates: { id: string; startTime: string; endTime: string }[]): Promise<AvailabilityResponse> {
    try {
      const response = await UHttp.put(
        apiUrlServer + "/api/timeslot/update-bulk",
        { companyId, updates },
        UHttp.authHeaders()
      );
      const responseData = await response.json();
      return { ...responseData, status: response.status };
    } catch (error) {
      throw error;
    }
  }

   /**
   * Récupère les créneaux disponibles d'un prestataire pour une date donnée
   * en tenant compte des rendez-vous existants et de la durée du service demandé
   */
  async getProfessionalAvailability(collaboratorId: string, serviceId: string, serviceDuration: number, date: string, bufferTime?: number, selectedOptionIds?: string[]): Promise<AvailabilityResponse> {
    try {
      const response = await UHttp.post(
          apiUrlServer + "/api/timeslot/collaborator/availability",
          {
            collaboratorCompagnieId: collaboratorId,
            serviceId,
            serviceDuration,
            date,
            bufferTime,
            selectedOptionIds: selectedOptionIds || []
          },
          UHttp.authHeaders()
      )

      const responseData = await response.json();
      return {
        ...responseData,
        status: response.status
      };
    } catch (error) {
      throw error;
    }
  }

}

